import { supabase } from '../lib/supabase';
import { Game } from './espnService';

export interface DatabaseGame {
  id?: number;
  espn_id: string;
  game_id: string;
  competition_id: string;
  week: number;
  season: number;
  home_team: string;
  away_team: string;
  home_team_abbr: string;
  away_team_abbr: string;
  home_team_logo: string;
  away_team_logo: string;
  game_date: string;
  home_spread: number;
  venue: string;
  home_score?: number;
  away_score?: number;
  is_final: boolean;
  status: string;
}

class DatabaseService {
  async saveGames(games: Game[]): Promise<{ success: number; errors: number }> {
    let successCount = 0;
    let errorCount = 0;

    console.log(`Saving ${games.length} games to database...`);

    // Convert to database format
    const dbGames: DatabaseGame[] = games.map(game => ({
      espn_id: game.espn_id,
      game_id: game.game_id,
      competition_id: game.competition_id,
      week: game.week,
      season: game.season,
      home_team: game.home_team,
      away_team: game.away_team,
      home_team_abbr: game.home_team_abbr,
      away_team_abbr: game.away_team_abbr,
      home_team_logo: game.home_team_logo,
      away_team_logo: game.away_team_logo,
      game_date: game.game_date,
      home_spread: game.home_spread,
      venue: game.venue,
      home_score: game.home_score,
      away_score: game.away_score,
      is_final: game.is_final,
      status: game.status
    }));

    // Save in batches to avoid overwhelming the database
    const batchSize = 10;
    for (let i = 0; i < dbGames.length; i += batchSize) {
      const batch = dbGames.slice(i, i + batchSize);
      
      try {
        const { error } = await supabase
          .from('games')
          .upsert(batch, {
            onConflict: 'espn_id',
            ignoreDuplicates: false
          });

        if (error) {
          console.error(`Error saving batch ${i / batchSize + 1}:`, error);
          errorCount += batch.length;
        } else {
          console.log(`Saved batch ${i / batchSize + 1} (${batch.length} games)`);
          successCount += batch.length;
        }
      } catch (error) {
        console.error(`Exception saving batch ${i / batchSize + 1}:`, error);
        errorCount += batch.length;
      }

      await new Promise(resolve => setTimeout(resolve, 50));
    }

    console.log(`Database save complete: ${successCount} successful, ${errorCount} errors`);
    return { success: successCount, errors: errorCount };
  }

  async getGames(season: number, week?: number): Promise<DatabaseGame[]> {
    let query = supabase
      .from('games')
      .select('*')
      .eq('season', season)
      .order('week', { ascending: true })
      .order('game_date', { ascending: true });

    if (week) {
      query = query.eq('week', week);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching games:', error);
      throw error;
    }

    return data || [];
  }

  async updateGameScores(espnId: string, homeScore: number, awayScore: number, isFinal: boolean, status: string): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({
        home_score: homeScore,
        away_score: awayScore,
        is_final: isFinal,
        status: status,
        updated_at: new Date().toISOString()
      })
      .eq('espn_id', espnId);

    if (error) {
      console.error(`Error updating game ${espnId}:`, error);
      throw error;
    }
  }

  async updateGameSpreads(gameId: string, homeSpread: number): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({
        home_spread: homeSpread,
        updated_at: new Date().toISOString()
      })
      .eq('game_id', gameId);

    if (error) {
      console.error(`Error updating spreads for game ${gameId}:`, error);
      throw error;
    }
  }

  async batchUpdateSpreads(updates: Array<{ gameId: string; homeSpread: number }>): Promise<{ success: number; errors: number }> {
    let successCount = 0;
    let errorCount = 0;

    console.log(`Updating spreads for ${updates.length} games...`);

    for (const update of updates) {
      try {
        await this.updateGameSpreads(update.gameId, update.homeSpread);
        successCount++;
      } catch (error) {
        console.error(`Failed to update spread for game ${update.gameId}:`, error);
        errorCount++;
      }
    }

    console.log(`Spread updates complete: ${successCount} successful, ${errorCount} errors`);
    return { success: successCount, errors: errorCount };
  }

  async clearSeasonGames(season: number): Promise<void> {
    const { error } = await supabase
      .from('games')
      .delete()
      .eq('season', season);

    if (error) {
      console.error(`Error clearing season ${season} games:`, error);
      throw error;
    }

    console.log(`Cleared all games for season ${season}`);
  }
}

export const databaseService = new DatabaseService();